import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, Circle, Lock, Clock, AlertCircle, 
  ChevronRight, Shield, Crown, Package, 
  FileCheck, Building, Target, Play, RefreshCw,
  Upload, Send, Sparkles, PartyPopper, ArrowRight,
  Calendar, AlertTriangle, X, ExternalLink, Calculator, Check
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ TIER CONFIGURATION - ALIGNED WITH PRICING PAGE ============
const BUILD_TIERS = {
  foundation: {
    name: 'Foundation',
    displayName: 'Foundation Package',
    price: 997,
    color: '#CD7F32', // Bronze
    supportDays: 30,
    features: [
      'LLC formation guidance',
      'EIN registration assistance',
      'DOT number filing guidance',
      'MC authority filing guidance',
      'BOC-3 coordination',
      'UCR guidance',
      'IRP & IFTA checklist',
      '30 Days Compliance Support',
      '30 Days Dispatch Support',
      'Email support'
    ],
    exclusiveFeatures: [], // Foundation is the base
    taskTier: 'basic'
  },
  accelerator: {
    name: 'Accelerator',
    displayName: 'Accelerator Package',
    price: 1997,
    color: '#C0C0C0', // Silver
    supportDays: 60,
    features: [
      'Everything in Foundation',
      'Drug & Alcohol Consortium setup',
      'Clearinghouse registration guidance',
      'Compliance calendar setup',
      '60 Days Compliance Monitoring',
      'Carrier packet template',
      '60 Days Dispatch Support',
      'Priority email support'
    ],
    exclusiveFeatures: [
      'Drug & Alcohol Consortium setup',
      'Clearinghouse registration',
      'Compliance calendar setup',
      'Carrier packet template',
      'Priority support'
    ],
    taskTier: 'professional'
  },
  elite: {
    name: 'Elite',
    displayName: 'Elite Package',
    price: 2997,
    color: '#D4AF37', // Gold
    supportDays: 90,
    features: [
      'Everything in Accelerator',
      'Business bank account setup guidance',
      'Factoring company assistance',
      'Fuel card setup guidance',
      'ELD consultation',
      '90 Days Compliance Support',
      'Business growth blueprint',
      '90 Days Dispatch Support',
      'Dedicated account manager'
    ],
    exclusiveFeatures: [
      'Business bank account setup',
      'Factoring company assistance',
      'Fuel card setup',
      'ELD consultation',
      'Business growth blueprint',
      'Dedicated account manager'
    ],
    taskTier: 'elite'
  }
};

// Phase configurations with icons
const PHASE_CONFIG = {
  1: { name: 'Intake', icon: FileCheck, description: 'Document collection and initial setup' },
  2: { name: 'Formation', icon: Building, description: 'Business entity formation guidance' },
  3: { name: 'Authority', icon: Target, description: 'MC authority application monitoring' },
  4: { name: 'Compliance Setup', icon: Shield, description: 'Compliance handoff and setup' },
  5: { name: 'Launch Activation', icon: Play, description: 'Final activation and launch support' }
};

// ============ CMS-DRIVEN TASK MAPPING ============
// Tasks are fetched from CMS API and mapped by phase
// This is the fallback structure - actual tasks come from database

const FALLBACK_PHASE_TASKS = {
  1: { // INTAKE & FOUNDATION SETUP
    basic: [
      { id: 'llc_formation', task_name: 'LLC Formation', description: 'Form your trucking LLC correctly' },
      { id: 'ein_registration', task_name: 'EIN Registration (Tax ID)', description: 'Get your business tax ID from IRS' }
    ],
    professional: [
      { id: 'business_banking_strategy', task_name: 'Business Banking Strategy', description: 'Set up trucking-optimized business banking' }
    ],
    elite: [
      { id: 'priority_onboarding_call', task_name: 'Priority Onboarding Strategy Call', description: 'Personalized launch strategy session' },
      { id: 'entity_structure_optimization', task_name: 'Entity Structure Optimization', description: 'Tax structure and S-Corp guidance' }
    ]
  },
  2: { // AUTHORITY REGISTRATION
    basic: [
      { id: 'usdot_filing', task_name: 'USDOT Number Registration', description: 'Register for federal operating identity' },
      { id: 'mc_authority', task_name: 'MC Authority Application', description: 'Apply for Motor Carrier authority' },
      { id: 'insurance_coordination', task_name: 'Commercial Insurance Coordination', description: 'Get proper trucking insurance coverage' }
    ],
    professional: [
      { id: 'factoring_prequalification', task_name: 'Factoring Pre-Qualification', description: 'Set up same-day payment solution' },
      { id: 'insurance_optimization', task_name: 'Insurance Coverage Optimization', description: 'Optimize coverage and find discounts' }
    ],
    elite: [
      { id: 'insurance_broker_matching', task_name: 'Premium Insurance Broker Matching', description: 'Get matched with vetted trucking insurance brokers' }
    ]
  },
  3: { // COMPLIANCE & REGISTRATION
    basic: [
      { id: 'boc3_filing', task_name: 'BOC-3 Process Agent Filing', description: 'Designate process agents for legal service' },
      { id: 'ucr_registration', task_name: 'UCR Registration', description: 'Complete annual federal carrier registration' },
      { id: 'drug_testing_enrollment', task_name: 'Drug & Alcohol Testing Enrollment', description: 'Enroll in required testing program' }
    ],
    professional: [
      { id: 'compliance_calendar_setup', task_name: 'Compliance Calendar Setup', description: 'Set up deadline tracking system' }
    ],
    elite: [
      { id: 'launch_coordinator_access', task_name: '30-Day Launch Coordinator Access', description: 'Dedicated support for your launch' }
    ]
  },
  4: { // AUTHORITY ACTIVATION
    basic: [
      { id: 'authority_tracking', task_name: 'Authority Activation Tracking', description: 'Monitor and verify authority status' }
    ],
    professional: [],
    elite: []
  },
  5: { // LAUNCH ACTIVATION
    basic: [
      { id: 'launch_activation', task_name: 'Launch Activation Checklist', description: 'Final preparation for first load' }
    ],
    professional: [
      { id: 'carrier_packet_professional', task_name: 'Professional Carrier Packet', description: 'Create professional broker documentation' }
    ],
    elite: [
      { id: 'first_load_coaching', task_name: 'First Load Coaching Session', description: 'Expert guidance for your first load' },
      { id: 'revenue_dashboard_setup', task_name: 'Revenue Dashboard Setup', description: 'Set up profit tracking system' }
    ]
  }
};

// Get tasks for a specific phase and tier (cumulative)
const getTasksForPhaseAndTier = (phase, tier) => {
  const phaseTasks = FALLBACK_PHASE_TASKS[phase];
  if (!phaseTasks) return [];
  
  const tierKey = (tier || 'basic').toLowerCase();
  
  // Foundation/basic tasks are always included
  let tasks = [...(phaseTasks.basic || [])];
  
  // Accelerator/professional tier includes basic + professional tasks
  if (tierKey === 'professional' || tierKey === 'accelerator' || tierKey === 'elite') {
    tasks = [...tasks, ...(phaseTasks.professional || [])];
  }
  
  // Elite tier includes basic + professional + elite tasks
  if (tierKey === 'elite') {
    tasks = [...tasks, ...(phaseTasks.elite || [])];
  }
  
  // Add default status to each task
  return tasks.map(task => ({
    ...task,
    status: 'pending'
  }));
};

// Get phase description with tier-specific info
const getPhaseDescription = (phase, tierInfo) => {
  if (phase === 5 && tierInfo) {
    return `Final activation and ${tierInfo.launchSupportDays}-day launch support`;
  }
  return PHASE_CONFIG[phase]?.description || '';
};

// Task status configurations
const TASK_STATUS_CONFIG = {
  locked: { icon: Lock, color: '#6B7280', label: 'Locked' },
  pending: { icon: Circle, color: '#6B7280', label: 'Pending' },
  submitted: { icon: Clock, color: '#F59E0B', label: 'Under Review' },
  needs_review: { icon: AlertCircle, color: '#F97316', label: 'Needs Attention' },
  approved: { icon: CheckCircle, color: '#10B981', label: 'Approved' },
  rejected: { icon: AlertCircle, color: '#EF4444', label: 'Rejected' }
};

// Compliance pricing
const COMPLIANCE_OPTIONS = [
  { tier: 'basic', name: 'Basic Monitoring', price: 197, features: ['Authority monitoring', 'Safety score tracking', 'Expiration alerts', 'Basic document vault', 'Monthly summary'] },
  { tier: 'managed', name: 'Managed Compliance', price: 297, features: ['Everything in Basic', 'IFTA & IRP assistance', 'UCR guidance', 'Compliance calendar', 'Audit readiness score'] },
  { tier: 'full_oversight', name: 'Full Oversight', price: 397, features: ['Everything in Managed', 'Filing handling', 'Insurance coordination', 'Mock audit support', 'Priority support'] }
];

// Dispatch pricing
const DISPATCH_OPTIONS = [
  { tier: 'bronze', name: 'Bronze Dispatch', price: 300, period: 'week', features: ['Dedicated dispatcher', 'Load search & booking', 'Basic route planning', 'Weekly load summary'] },
  { tier: 'silver', name: 'Silver Dispatch', price: 400, period: 'week', features: ['Everything in Bronze', 'Rate negotiation', 'RPM optimization', 'Weekly KPI reporting'] },
  { tier: 'gold', name: 'Gold Dispatch', price: 500, period: 'week', features: ['Everything in Silver', 'Priority load access', 'Lane strategy', 'Monthly performance reviews'] }
];

const BUNDLE_DISCOUNT = 20;

// Grace Period Banner
const GracePeriodBanner = ({ daysRemaining, endDate, onSelectPlan }) => (
  <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/40 p-4 mb-6">
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <Calendar className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-[#D4AF37] font-bold text-lg">Grace Period Active</h3>
          <p className="text-white/70 text-sm">
            <span className="text-white font-bold">{daysRemaining} days</span> remaining to select your operations plan
          </p>
          {endDate && (
            <p className="text-white/50 text-xs mt-1">
              Ends: {new Date(endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onSelectPlan}
        className="px-4 py-2 bg-[#D4AF37] text-black font-bold text-sm hover:bg-[#F5D47B] transition-colors flex items-center gap-2"
      >
        Select Plan
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// Build Completed Summary Card
const BuildCompletedCard = ({ completedAt, tier }) => (
  <div className="bg-[#121212] border border-emerald-500/30 p-6 mb-6" data-testid="build-completed-card">
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 bg-emerald-500/20 flex items-center justify-center">
        <CheckCircle className="w-7 h-7 text-emerald-400" />
      </div>
      <div className="flex-1">
        <h3 className="text-emerald-400 font-bold text-xl mb-1">Build Complete</h3>
        <p className="text-white/60 text-sm">
          Your authority has been activated. You've successfully completed the Build phase.
        </p>
        {completedAt && (
          <p className="text-white/40 text-xs mt-2">
            Completed: {new Date(completedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}
        {tier && (
          <span className="inline-block mt-2 px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium uppercase">
            {tier} Package
          </span>
        )}
      </div>
    </div>
  </div>
);

// Plan Selection Card
const PlanCard = ({ option, type, isSelected, onSelect, bundleActive }) => {
  const price = bundleActive ? option.price * (1 - BUNDLE_DISCOUNT / 100) : option.price;
  const originalPrice = option.price;
  
  return (
    <div 
      className={`border p-4 cursor-pointer transition-all ${
        isSelected 
          ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
          : 'border-zinc-800 hover:border-zinc-700'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            type === 'compliance' ? 'text-[#C0C0C0]' : 'text-[#D4AF37]'
          }`}>
            {option.name}
          </span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-white">${Math.round(price)}</span>
            <span className="text-white/50">/mo</span>
            {bundleActive && (
              <span className="text-emerald-400 text-xs line-through">${originalPrice}</span>
            )}
          </div>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
          isSelected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-zinc-600'
        }`}>
          {isSelected && <CheckCircle className="w-3 h-3 text-black" />}
        </div>
      </div>
      <ul className="space-y-1">
        {option.features.slice(0, 3).map((f, i) => (
          <li key={i} className="text-white/60 text-xs flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-[#D4AF37]" />
            {f}
          </li>
        ))}
        {option.features.length > 3 && (
          <li className="text-white/40 text-xs">+{option.features.length - 3} more features</li>
        )}
      </ul>
    </div>
  );
};

// Post-Authority Plan Selection Modal/Screen
const PlanSelectionScreen = ({ onClose, onConfirm, currentSelection }) => {
  const [complianceTier, setComplianceTier] = useState(currentSelection?.compliance_tier || null);
  const [dispatchTier, setDispatchTier] = useState(currentSelection?.dispatch_tier || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const isBundle = complianceTier && dispatchTier;
  
  const compliancePrice = complianceTier ? COMPLIANCE_OPTIONS.find(o => o.tier === complianceTier)?.price || 0 : 0;
  const dispatchPrice = dispatchTier ? DISPATCH_OPTIONS.find(o => o.tier === dispatchTier)?.price || 0 : 0;
  const totalBeforeDiscount = compliancePrice + dispatchPrice;
  const discountAmount = isBundle ? totalBeforeDiscount * (BUNDLE_DISCOUNT / 100) : 0;
  const totalAfterDiscount = totalBeforeDiscount - discountAmount;
  
  const handleConfirm = async () => {
    if (!complianceTier && !dispatchTier) return;
    
    setIsSubmitting(true);
    try {
      await onConfirm({
        compliance_tier: complianceTier,
        dispatch_tier: dispatchTier,
        is_bundle: isBundle
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0A0A0A] border border-zinc-800 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <PartyPopper className="w-6 h-6 text-[#D4AF37]" />
              <h2 className="text-2xl font-bold text-white">Authority Activated!</h2>
            </div>
            <p className="text-white/60">Choose your operations plan to continue</p>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/40 hover:text-white p-2">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
        
        <div className="p-6 space-y-6">
          {/* Bundle Notice */}
          {isBundle && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <div>
                <span className="text-emerald-400 font-bold">Bundle Discount Applied!</span>
                <span className="text-white/70 text-sm ml-2">Save {BUNDLE_DISCOUNT}% when you select both services</span>
              </div>
            </div>
          )}
          
          {/* Compliance Options */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-[#C0C0C0]" />
              <h3 className="text-white font-bold">Option 1: Compliance Monitoring</h3>
              <span className="text-white/40 text-sm">(Protect)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {COMPLIANCE_OPTIONS.map(option => (
                <PlanCard 
                  key={option.tier}
                  option={option}
                  type="compliance"
                  isSelected={complianceTier === option.tier}
                  onSelect={() => setComplianceTier(complianceTier === option.tier ? null : option.tier)}
                  bundleActive={isBundle}
                />
              ))}
            </div>
          </div>
          
          {/* Dispatch Options */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="text-white font-bold">Option 2: Dispatch Services</h3>
              <span className="text-white/40 text-sm">(Grow)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {DISPATCH_OPTIONS.map(option => (
                <PlanCard 
                  key={option.tier}
                  option={option}
                  type="dispatch"
                  isSelected={dispatchTier === option.tier}
                  onSelect={() => setDispatchTier(dispatchTier === option.tier ? null : option.tier)}
                  bundleActive={isBundle}
                />
              ))}
            </div>
          </div>
          
          {/* Summary */}
          <div className="bg-[#121212] border border-zinc-800 p-6">
            <h4 className="text-white font-bold mb-4">Order Summary</h4>
            <div className="space-y-2 text-sm">
              {complianceTier && (
                <div className="flex justify-between">
                  <span className="text-white/70">Compliance - {COMPLIANCE_OPTIONS.find(o => o.tier === complianceTier)?.name}</span>
                  <span className="text-white">${compliancePrice}/mo</span>
                </div>
              )}
              {dispatchTier && (
                <div className="flex justify-between">
                  <span className="text-white/70">Dispatch - {DISPATCH_OPTIONS.find(o => o.tier === dispatchTier)?.name}</span>
                  <span className="text-white">${dispatchPrice}/mo</span>
                </div>
              )}
              {isBundle && (
                <div className="flex justify-between text-emerald-400">
                  <span>Bundle Discount ({BUNDLE_DISCOUNT}%)</span>
                  <span>-${Math.round(discountAmount)}</span>
                </div>
              )}
              <div className="border-t border-zinc-700 pt-2 mt-2 flex justify-between">
                <span className="text-white font-bold">Monthly Total</span>
                <span className="text-[#D4AF37] font-bold text-xl">${Math.round(totalAfterDiscount)}/mo</span>
              </div>
            </div>
          </div>
          
          {/* Warning */}
          <div className="bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm">No Automatic Billing</p>
              <p className="text-white/60 text-xs mt-1">
                Your selection will be saved. Our team will contact you to finalize and activate your subscription.
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-zinc-800 flex items-center justify-between sticky bottom-0 bg-[#0A0A0A]">
          <p className="text-white/40 text-sm">
            {!complianceTier && !dispatchTier ? 'Select at least one option' : 'Ready to confirm'}
          </p>
          <div className="flex gap-3">
            {onClose && (
              <button 
                onClick={onClose}
                className="px-6 py-3 border border-zinc-700 text-white font-medium hover:bg-zinc-900"
              >
                Maybe Later
              </button>
            )}
            <button 
              onClick={handleConfirm}
              disabled={(!complianceTier && !dispatchTier) || isSubmitting}
              className={`px-6 py-3 font-bold transition-colors ${
                (complianceTier || dispatchTier) && !isSubmitting
                  ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]'
                  : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Saving...' : 'Confirm Selection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Phase Card Component
const PhaseCard = ({ phase, phaseData, isExpanded, onToggle, isLocked, isCompleted, tierInfo, tier, tierConfig, onTaskClick }) => {
  const config = PHASE_CONFIG[phase];
  const Icon = config?.icon || Circle;
  const isCurrent = phaseData?.is_current;
  const tierColor = tierConfig?.color || '#D4AF37';
  
  // Get tier-based tasks for this phase
  const tierTasks = getTasksForPhaseAndTier(phase, tier);
  
  // Merge with any existing task statuses from phaseData
  const existingTasks = phaseData?.tasks || [];
  const tasks = tierTasks.map(task => {
    const existingTask = existingTasks.find(t => t.id === task.id || t.task_name === task.task_name);
    return existingTask ? { ...task, ...existingTask } : task;
  });
  
  const completedTasks = tasks.filter(t => t.status === 'approved' || t.status === 'completed').length;
  const totalTasks = tasks.length;
  
  // Get tier-specific description for Phase 5
  const phaseDescription = getPhaseDescription(phase, tierInfo);
  
  return (
    <div 
      className={`border transition-all duration-300 ${
        isCompleted
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : isCurrent 
            ? 'bg-opacity-5' 
            : isLocked 
              ? 'border-zinc-800 bg-zinc-900/50 opacity-60' 
              : 'border-zinc-800 bg-zinc-900'
      }`}
      style={isCurrent && !isCompleted ? { 
        borderColor: `${tierColor}50`,
        backgroundColor: `${tierColor}08`
      } : undefined}
      data-testid={`phase-${phase}-card`}
    >
      <button
        onClick={onToggle}
        disabled={isLocked}
        className="w-full p-4 sm:p-5 flex items-center justify-between gap-4"
      >
        <div className="flex items-center gap-4">
          <div 
            className={`w-12 h-12 flex items-center justify-center ${
              isCompleted ? 'bg-emerald-500/20' : isLocked ? 'bg-zinc-800' : 'bg-zinc-800'
            }`}
            style={isCurrent && !isCompleted ? { backgroundColor: `${tierColor}20` } : undefined}
          >
            {isLocked ? (
              <Lock className="w-5 h-5 text-zinc-600" />
            ) : isCompleted ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <Icon className={`w-5 h-5 ${!isCurrent ? 'text-zinc-400' : ''}`} style={isCurrent ? { color: tierColor } : undefined} />
            )}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span 
                className={`text-lg font-bold ${
                  isCompleted ? 'text-emerald-400' : isLocked ? 'text-zinc-600' : 'text-white'
                }`}
                style={isCurrent && !isCompleted ? { color: tierColor } : undefined}
              >
                Phase {phase}
              </span>
              {isCurrent && (
                <span 
                  className="px-2 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
                >
                  CURRENT
                </span>
              )}
              {isCompleted && (
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                  COMPLETE
                </span>
              )}
            </div>
            <p className={`text-sm ${isLocked ? 'text-zinc-700' : 'text-zinc-400'}`}>
              {config?.name}
              {phase === 5 && tierInfo && (
                <span className="ml-2" style={{ color: tierColor }}>({tierInfo.launchSupportDays}-Day Launch Support)</span>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {!isLocked && totalTasks > 0 && (
            <div className="text-right">
              <p className="text-white font-medium">{completedTasks}/{totalTasks}</p>
              <p className="text-zinc-500 text-xs">tasks</p>
            </div>
          )}
          {!isLocked && (
            <ChevronRight className={`w-5 h-5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          )}
        </div>
      </button>
      
      {/* Expanded Task List */}
      {isExpanded && !isLocked && tasks.length > 0 && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5 border-t border-zinc-800">
          <div className="mt-4 space-y-3">
            {tasks.map((task, idx) => {
              const taskConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
              const TaskIcon = taskConfig.icon;
              const taskId = task.task_id || task.id || task.task_name?.toLowerCase().replace(/\s+/g, '_');
              
              return (
                <button 
                  key={task.id || idx} 
                  onClick={() => onTaskClick && onTaskClick(taskId)}
                  className="w-full flex items-start gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors text-left group"
                  data-testid={`task-${taskId}`}
                >
                  <TaskIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: taskConfig.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium text-sm group-hover:text-[#D4AF37] transition-colors">{task.task_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-zinc-700" style={{ color: taskConfig.color }}>
                        {taskConfig.label}
                      </span>
                    </div>
                    {task.description && (
                      <p className="text-zinc-500 text-xs mt-1">{task.description}</p>
                    )}
                    {task.admin_notes && (
                      <p className="text-amber-400 text-xs mt-1 italic">Admin: {task.admin_notes}</p>
                    )}
                    {task.completed_at && (
                      <p className="text-emerald-400 text-xs mt-1">
                        Completed: {new Date(task.completed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Build Progress Module
const BuildProgressModule = ({ token, starterPackTier, userType }) => {
  const navigate = useNavigate();
  const [lifecycleStatus, setLifecycleStatus] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showTierInfo, setShowTierInfo] = useState(false);
  
  // Handle task click - navigate to task execution page
  const handleTaskClick = (taskId) => {
    navigate(`/hive/build/task/${taskId}`);
  };
  
  // Determine tier from props or lifecycle status
  // IMPORTANT: Always use lowercase for tier matching
  const getTierKey = () => {
    // Priority: lifecycle status (API) > starterPackTier prop > default
    const tier = lifecycleStatus?.build?.tier || 
                 starterPackTier?.name?.toLowerCase() || 
                 'foundation';
    return tier.toLowerCase();
  };
  
  const getTierDisplayName = () => {
    const tierKey = getTierKey();
    if (tierKey === 'elite') return 'Elite';
    if (tierKey === 'professional' || tierKey === 'accelerator') return 'Accelerator';
    return 'Foundation';
  };
  
  const getTierConfig = () => {
    const tierKey = getTierKey();
    if (tierKey === 'elite') return BUILD_TIERS.elite;
    if (tierKey === 'professional' || tierKey === 'accelerator') return BUILD_TIERS.accelerator;
    return BUILD_TIERS.foundation;
  };
  
  const getTierInfo = () => {
    const tierKey = getTierKey();
    if (tierKey === 'elite') return { name: 'Elite', launchSupportDays: 90, price: 2997 };
    if (tierKey === 'professional' || tierKey === 'accelerator') return { name: 'Accelerator', launchSupportDays: 60, price: 1997 };
    return { name: 'Foundation', launchSupportDays: 30, price: 997 };
  };
  
  const tierInfo = getTierInfo();
  const tierConfig = getTierConfig();
  const tier = getTierKey();  // Always lowercase for task matching
  const tierDisplayName = getTierDisplayName();  // For UI display
  
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchLifecycleStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/hive/me/lifecycle-status`, { headers });
      setLifecycleStatus(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching lifecycle status:', err);
      return null;
    }
  }, [token]);

  const fetchProgressData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/starter-pack/me`, { headers });
      setProgressData(response.data);
      
      // Auto-expand current phase
      if (response.data?.progress?.current_phase) {
        setExpandedPhase(response.data.progress.current_phase);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error fetching progress:', err);
      }
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const status = await fetchLifecycleStatus();
        
        // Only fetch progress if user is a build client
        if (status?.build?.show_tracker || status?.build?.show_completed_summary) {
          await fetchProgressData();
        }
      } catch (err) {
        setError('Failed to load build status');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [fetchLifecycleStatus, fetchProgressData]);

  // Calculate overall progress based on tier-specific tasks
  const calculateProgress = () => {
    const currentPhase = progressData?.progress?.current_phase || lifecycleStatus?.build?.current_phase || 1;
    let totalTasks = 0;
    let completedTasks = 0;
    
    for (let phase = 1; phase <= 5; phase++) {
      const phaseTasks = getTasksForPhaseAndTier(phase, tier);
      const existingPhaseTasks = progressData?.progress?.phases?.[phase]?.tasks || [];
      
      phaseTasks.forEach(task => {
        totalTasks++;
        const existingTask = existingPhaseTasks.find(t => t.id === task.id || t.task_name === task.task_name);
        if (existingTask?.status === 'approved' || existingTask?.status === 'completed' || phase < currentPhase) {
          completedTasks++;
        }
      });
    }
    
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const handleSelectPlan = async (selection) => {
    try {
      await axios.post(`${API}/hive/subscription/select`, selection, { headers });
      setShowPlanSelection(false);
      // Refresh lifecycle status
      await fetchLifecycleStatus();
    } catch (err) {
      console.error('Error selecting plan:', err);
      alert('Failed to save plan selection. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  // ROLE-BASED VISIBILITY LOGIC
  
  // If lifecycle status says hide build completely (non-build client)
  if (lifecycleStatus?.build?.hide_completely) {
    return null; // Don't render anything
  }

  // If build is completed, show summary card only
  if (lifecycleStatus?.build?.show_completed_summary) {
    return (
      <div data-testid="build-completed-module">
        {/* Grace Period Banner */}
        {lifecycleStatus?.grace_period?.show_countdown && (
          <GracePeriodBanner 
            daysRemaining={lifecycleStatus.grace_period.days_remaining}
            endDate={lifecycleStatus.grace_period.end_date}
            onSelectPlan={() => setShowPlanSelection(true)}
          />
        )}
        
        {/* Completed Summary */}
        <BuildCompletedCard 
          completedAt={lifecycleStatus.build.completed_at}
          tier={lifecycleStatus.build.tier}
        />
        
        {/* Show Plan Selection if needed */}
        {lifecycleStatus?.show_plan_selection && (
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-6 mb-6">
            <h3 className="text-[#D4AF37] font-bold text-lg mb-2">Next Step: Choose Your Operations Plan</h3>
            <p className="text-white/60 text-sm mb-4">
              Your authority is active. Select your ongoing services to continue.
            </p>
            <button
              onClick={() => setShowPlanSelection(true)}
              className="px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors flex items-center gap-2"
            >
              View Plan Options
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Plan Selection Modal */}
        {showPlanSelection && (
          <PlanSelectionScreen 
            onClose={() => setShowPlanSelection(false)}
            onConfirm={handleSelectPlan}
            currentSelection={lifecycleStatus?.subscriptions}
          />
        )}
      </div>
    );
  }

  // If no signature program data and not a build client
  if (!progressData && !lifecycleStatus?.user_type?.is_build_client) {
    return (
      <div className="p-8 text-center" data-testid="no-starter-pack">
        <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">No Active Build Program</h3>
        <p className="text-zinc-500 text-sm mb-4">
          You don't have an active Signature Program. Start building your authority today.
        </p>
        <a 
          href="/build"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#F5D47B] transition-colors"
        >
          Learn About Build
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    );
  }

  // Show full phase tracker for active build clients
  const progress = progressData?.progress || {};
  const phases = progress.phases || {};
  const currentPhase = progress.current_phase || lifecycleStatus?.build?.current_phase || 1;
  const percentComplete = calculateProgress();

  // Count total tier-specific tasks
  const getTotalTasksForTier = () => {
    let total = 0;
    for (let phase = 1; phase <= 5; phase++) {
      total += getTasksForPhaseAndTier(phase, tier).length;
    }
    return total;
  };

  return (
    <div data-testid="build-progress-module">
      {/* Grace Period Banner */}
      {lifecycleStatus?.grace_period?.show_countdown && (
        <GracePeriodBanner 
          daysRemaining={lifecycleStatus.grace_period.days_remaining}
          endDate={lifecycleStatus.grace_period.end_date}
          onSelectPlan={() => setShowPlanSelection(true)}
        />
      )}
      
      {/* Tier Header Badge */}
      <div className="bg-[#121212] border border-zinc-800 p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 flex items-center justify-center"
              style={{ backgroundColor: `${tierConfig.color}20`, border: `1px solid ${tierConfig.color}40` }}
            >
              <Crown className="w-6 h-6" style={{ color: tierConfig.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span 
                  className="px-3 py-1 text-xs font-bold uppercase tracking-wider"
                  style={{ backgroundColor: `${tierConfig.color}20`, color: tierConfig.color }}
                >
                  {tierConfig.displayName}
                </span>
                <span className="text-zinc-500 text-sm">${tierConfig.price.toLocaleString()} one-time</span>
              </div>
              <p className="text-zinc-400 text-sm mt-1">
                {tierConfig.supportDays}-Day Launch Support • {getTotalTasksForTier()} Tasks Included
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowTierInfo(!showTierInfo)}
              className="px-4 py-2 bg-zinc-800 text-white/70 hover:text-white text-sm transition-colors"
            >
              {showTierInfo ? 'Hide' : 'View'} Package Details
            </button>
            <button
              onClick={() => navigate('/hive/tools')}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-[#D4AF37] transition-colors"
            >
              <Calculator className="w-4 h-4" />
              <span className="text-sm font-medium hidden sm:inline">Tools</span>
            </button>
          </div>
        </div>
        
        {/* Expandable Tier Features */}
        {showTierInfo && (
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Your Package Features */}
              <div className="bg-zinc-900/50 p-4 border border-zinc-800">
                <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" style={{ color: tierConfig.color }} />
                  Your Package Includes
                </h4>
                <ul className="space-y-2">
                  {tierConfig.features.slice(0, 6).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs">
                      <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: tierConfig.color }} />
                      <span className="text-white/70">{feature}</span>
                    </li>
                  ))}
                  {tierConfig.features.length > 6 && (
                    <li className="text-white/40 text-xs">+{tierConfig.features.length - 6} more features</li>
                  )}
                </ul>
              </div>
              
              {/* Tier-Specific Exclusive Features (Accelerator/Elite only) */}
              {tierConfig.exclusiveFeatures.length > 0 && (
                <div className="bg-zinc-900/50 p-4 border border-zinc-800">
                  <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    {tierConfig.name} Exclusive
                  </h4>
                  <ul className="space-y-2">
                    {tierConfig.exclusiveFeatures.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs">
                        <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#D4AF37]" />
                        <span className="text-white/70">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Support Comparison */}
              <div className="bg-zinc-900/50 p-4 border border-zinc-800">
                <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-400" />
                  Support Period
                </h4>
                <div className="space-y-3">
                  {Object.entries(BUILD_TIERS).map(([key, t]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className={`text-xs ${tier === key || (tier === 'professional' && key === 'accelerator') ? 'text-white font-medium' : 'text-white/40'}`}>
                        {t.name}
                      </span>
                      <span 
                        className={`text-xs px-2 py-0.5 ${tier === key || (tier === 'professional' && key === 'accelerator') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-white/40'}`}
                      >
                        {t.supportDays} Days
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Progress Overview */}
      <div className="bg-[#121212] border border-zinc-800 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-bold text-lg">Build Progress</h3>
            <p className="text-zinc-500 text-sm">Phase {currentPhase} of 5 • {tierConfig.name} Track</p>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold" style={{ color: tierConfig.color }}>{percentComplete}%</span>
            <p className="text-zinc-500 text-xs">Complete</p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="h-2 bg-zinc-800 overflow-hidden">
          <div 
            className="h-full transition-all duration-500"
            style={{ width: `${percentComplete}%`, backgroundColor: tierConfig.color }}
          />
        </div>
      </div>
      
      {/* Phase Cards */}
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map(phase => {
          const phaseData = phases[phase] || {};
          const isLocked = phase > currentPhase;
          const isCompleted = phaseData.status === 'completed' || phase < currentPhase;
          
          return (
            <PhaseCard
              key={phase}
              phase={phase}
              phaseData={phaseData}
              isExpanded={expandedPhase === phase}
              onToggle={() => setExpandedPhase(expandedPhase === phase ? null : phase)}
              isLocked={isLocked}
              isCompleted={isCompleted}
              tierInfo={tierInfo}
              tier={tier}
              tierConfig={tierConfig}
              onTaskClick={handleTaskClick}
            />
          );
        })}
      </div>
      
      {/* Plan Selection Modal */}
      {showPlanSelection && (
        <PlanSelectionScreen 
          onClose={() => setShowPlanSelection(false)}
          onConfirm={handleSelectPlan}
          currentSelection={lifecycleStatus?.subscriptions}
        />
      )}
    </div>
  );
};

export default BuildProgressModule;
